/**
 * Every board in the room, at reading distance.
 *
 * The same snapshot the admin dashboard draws BoardsPanel from, cut for a
 * different reader. Two things are deliberately NOT here:
 *
 *   - DEVICE STATUS. Unpaired / Offline / "3 unsynced" is information for the
 *     person who can go and do something about it. Telling a room full of
 *     players that a tablet is sulking invites twenty of them to have an
 *     opinion about it, and the marker on that board already knows.
 *   - THE FORFEIT BUTTON. Nothing on this screen is clickable; it is a wall.
 *
 * Averages stay hidden until a match is over BY DEFAULT, for the reason
 * BoardCard gives: mid-match an average swings wildly on a single visit. A
 * venue can override that per-venue with tv_live_averages — see showLiveAverages
 * below. The default is unchanged for everybody who has not asked.
 */

import { throwingEntryId, matchAverages } from '../../lib/tournamentRoom.js';
import { formatAvg } from '../../lib/format.js';

/**
 * Rounds count DOWN to the final, so the number is the bracket's exponent.
 *
 * A match row has a group_id and no group NUMBER, so the group's name comes
 * from the standings, where the room already pairs a group number with the
 * board it plays on.
 */
function stageLabel(match, groupNumber) {
  if (!match) return null;
  if (match.stage !== 'knockout') {
    return groupNumber != null ? `Group ${groupNumber}` : 'Group';
  }

  switch (match.round) {
    case 1:  return 'Final';
    case 2:  return 'Semi-Final';
    case 3:  return 'Quarter-Final';
    default: return `Last ${2 ** (match.round ?? 1)}`;
  }
}

/* ------------------------------------------------------------------------- */

function TvPlayer({ name, legs, remaining, throwing, average }) {
  return (
    <div className={`hd-tv-player ${throwing ? 'hd-tv-throwing' : ''}`}>
      {/* The tint says "throwing" only to someone who can see it and knows the
          convention. At distance, in a dim room, that is a smaller group than
          it looks — so the marker is a shape as well as a colour. */}
      <span className="hd-tv-arrow" aria-hidden="true">{throwing ? '\u25B8' : ''}</span>

      <span className="hd-tv-name">{name ?? 'TBC'}</span>

      {average != null && (
        <span className="hd-tv-avg">{formatAvg(average)}</span>
      )}

      <span className="hd-tv-legs">{legs ?? 0}</span>

      <span className="hd-tv-remaining">
        {remaining != null ? remaining : '\u2014'}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function TvBoardCard({ board, completedMatch, groupNumber, showLiveAverages }) {
  // The room stops reporting a match the moment it ends; the hook holds the
  // finished one so the board does not blank between matches.
  const match   = board.match ?? completedMatch ?? null;
  const isLive  = match?.status === 'live';
  const upcoming = board.upcoming?.[0] ?? null;

  // finalAverages comes off match_completed and is the whole match; deriving
  // from the held snapshot would miss the deciding leg. See tournamentRoom.js.
  //
  // A LIVE average is derived, never final — there is no finalAverages until
  // the match ends, and the running figure is exactly what the venue asked to
  // see. Off unless the venue turned tv_live_averages on, which keeps the
  // default this component was written with: an average mid-match swings
  // wildly on a single visit, and a wall is a bad place to watch that happen.
  const averages = match
    ? (isLive
        ? (showLiveAverages ? matchAverages(match) : {})
        : match.finalAverages ?? matchAverages(match))
    : {};
  const throwing = throwingEntryId(match);

  const legsWon = match?.legsWon ?? {};

  // One lookup for both: the live leg carries the remaining scores AND the
  // number to print. There is no currentLegNumber on a snapshot — only
  // currentLegId — so the number has to come from the leg itself.
  const currentLeg = isLive
    ? (match.legs ?? []).find((l) => l.id === match.currentLegId) ?? null
    : null;

  const remaining = currentLeg?.remaining ?? {};

  return (
    <div className={`hd-tv-board ${isLive ? 'hd-tv-board-live' : ''}`}>
      <div className="hd-tv-board-head">
        <span className="hd-tv-board-number">Board {board.boardNumber}</span>
        {match && (
          <span className="hd-tv-board-stage">{stageLabel(match, groupNumber)}</span>
        )}
      </div>

      {match ? (
        <>
          <div className="hd-tv-players">
            {[match.player1_id, match.player2_id].map((entryId, i) => (
              <TvPlayer
                key={entryId ?? `slot-${i}`}
                name={i === 0 ? match.player1_name : match.player2_name}
                legs={legsWon[entryId]}
                remaining={remaining[entryId]}
                throwing={throwing != null && throwing === entryId}
                average={averages[entryId]}
              />
            ))}
          </div>

          <div className="hd-tv-board-foot">
            {isLive
              ? `Leg ${currentLeg?.leg_number ?? '\u2014'} \u00B7 first to ${match.legsToWin}`
              : 'Match complete'}
          </div>
        </>
      ) : (
        <div className="hd-tv-board-idle">
          {upcoming
            ? `Next: ${upcoming.player1_name ?? 'TBC'} v ${upcoming.player2_name ?? 'TBC'}`
            : 'Waiting'}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * The grid sizes itself to the board count: four boards on a pub TV want to be
 * four big cards, not four small ones in the corner of a twelve-slot grid.
 */
export default function TvBoards({
  boards, completedByBoard, groupByBoard = {}, showLiveAverages = false,
}) {
  if (!boards.length) return null;

  const columns = boards.length <= 2 ? 1 : boards.length <= 6 ? 2 : 3;

  return (
    <div className="hd-tv-boards" data-columns={columns}>
      {boards.map((board) => (
        <TvBoardCard
          key={board.boardNumber}
          board={board}
          completedMatch={completedByBoard[board.boardNumber] ?? null}
          groupNumber={groupByBoard[board.boardNumber] ?? null}
          showLiveAverages={showLiveAverages}
        />
      ))}
    </div>
  );
}
